import { useQuery } from '@tanstack/react-query'
import { msalInstance } from '@/features/auth/lib/msalConfig'
import { InteractionRequiredAuthError } from '@azure/msal-browser'

const GRAPH_PHOTO_SCOPES = ['https://graph.microsoft.com/User.ReadBasic.All']

async function fetchGraphPhoto(email: string): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) throw new Error('no account')

  let token: string
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: GRAPH_PHOTO_SCOPES,
      account,
    })
    token = result.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await msalInstance.acquireTokenPopup({
        scopes: GRAPH_PHOTO_SCOPES,
        account,
      })
      token = result.accessToken
    } else {
      throw err
    }
  }

  const res = await fetch(
    `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}/photo/$value`,
    { headers: { Authorization: `Bearer ${token}` } },
  )
  if (!res.ok) throw new Error(`HTTP ${res.status}`)

  const blob = await res.blob()
  return URL.createObjectURL(blob)
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Deterministic pastel color from email string
function avatarColor(email: string): string {
  const colors = [
    'bg-violet-200 text-violet-800',
    'bg-blue-200 text-blue-800',
    'bg-teal-200 text-teal-800',
    'bg-rose-200 text-rose-800',
    'bg-amber-200 text-amber-800',
    'bg-emerald-200 text-emerald-800',
    'bg-pink-200 text-pink-800',
    'bg-indigo-200 text-indigo-800',
  ]
  let hash = 0
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

interface UserAvatarProps {
  email: string
  displayName: string
  size?: number
}

export default function UserAvatar({ email, displayName, size = 32 }: UserAvatarProps) {
  const { data: photoUrl } = useQuery({
    queryKey: ['graph-photo', email],
    queryFn: () => fetchGraphPhoto(email),
    retry: false,
    staleTime: 1000 * 60 * 30,
    enabled: !!email,
  })

  const initials = getInitials(displayName)
  const colorClass = avatarColor(email)
  const style = { width: size, height: size, minWidth: size, fontSize: size * 0.38 }

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={displayName}
        style={style}
        className="rounded-full object-cover shrink-0"
      />
    )
  }

  return (
    <div
      style={style}
      className={`rounded-full flex items-center justify-center font-semibold shrink-0 ${colorClass}`}
    >
      {initials}
    </div>
  )
}
